import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2 } from 'lucide-react';

function CompanyManagement() {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    medium: '',
    company_name: '',
    client_name: '',
    is_business: false,
    business_number: '',
    phone: '',
    email: '',
    address: ''
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    let filtered = companies;

    if (searchTerm) {
      filtered = filtered.filter(company =>
        company.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.medium.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCompanies(filtered);
  }, [companies, searchTerm]);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const [companiesResponse, projectsResponse] = await Promise.all([
        fetch(`${baseUrl}/api/companies`),
        fetch(`${baseUrl}/api/projects`)
      ]);
      
      const companiesData = await companiesResponse.json();
      const projectsData = await projectsResponse.json();
      
      // Create a map of company_id to their most recent contract date
      const companyLatestDates = {};
      projectsData.forEach(project => {
        if (project.contract_date && project.company_id) {
          const contractDate = new Date(project.contract_date);
          if (!companyLatestDates[project.company_id] || 
              contractDate > new Date(companyLatestDates[project.company_id])) {
            companyLatestDates[project.company_id] = project.contract_date;
          }
        }
      });
      
      // Sort companies by most recent contract date, then by created_at as fallback
      const sortedCompanies = companiesData.sort((a, b) => {
        const dateA = companyLatestDates[a.id] ? new Date(companyLatestDates[a.id]) : new Date(a.created_at);
        const dateB = companyLatestDates[b.id] ? new Date(companyLatestDates[b.id]) : new Date(b.created_at);
        return dateB - dateA; // Descending order
      });
      
      setCompanies(sortedCompanies);
      setFilteredCompanies(sortedCompanies);
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const url = editingCompany 
        ? `${baseUrl}/api/companies/${editingCompany.id}`
        : `${baseUrl}/api/companies`;
      
      const method = editingCompany ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowForm(false);
        setEditingCompany(null);
        resetForm();
        loadCompanies();
      } else {
        throw new Error('Failed to save company');
      }
    } catch (error) {
      console.error('Error saving company:', error);
      alert('Failed to save company');
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      medium: company.medium,
      company_name: company.company_name,
      client_name: company.client_name,
      is_business: company.is_business,
      business_number: company.business_number || '',
      phone: company.phone || '',
      email: company.email || '',
      address: company.address || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this company?')) {
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const response = await fetch(`${baseUrl}/api/companies/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        loadCompanies();
      } else {
        throw new Error('Failed to delete company');
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      alert('Failed to delete company');
    }
  };

  const resetForm = () => {
    setFormData({
      medium: '',
      company_name: '',
      client_name: '',
      is_business: false,
      business_number: '',
      phone: '',
      email: '',
      address: ''
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCompany(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">데이터를 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="page-container company-management-table">
      <div className="page-header">
        <h1>업체 관리</h1>
        <p>클라이언트 및 업체 정보 관리</p>
      </div>

      <div className="controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5em', flexWrap: 'nowrap' }}>
        <div className="search-box" style={{ display: 'flex', alignItems: 'center' }}>
          <Search size={20} />
          <input
            type="text"
            placeholder="업체명, 클라이언트명, Medium으로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input"
            style={{ minWidth: 180, marginRight: 0 }}
          />
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
          style={{ whiteSpace: 'nowrap', marginLeft: 'auto' }}
        >
          <Plus size={16} />
          새 업체
        </button>
      </div>

      {showForm && (
        <div className="form-overlay">
          <div className="form-modal">
            <h2>{editingCompany ? '업체 수정' : '새 업체 추가'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Medium</label>
                  <select
                    value={formData.medium}
                    onChange={(e) => setFormData({...formData, medium: e.target.value})}
                    className="form-input"
                    required
                  >
                    <option value="">Medium 선택</option>
                    <option value="카톡">카톡</option>
                    <option value="오픈챗">오픈챗</option>
                    <option value="숨고">숨고</option>
                    <option value="채널">채널</option>
                    <option value="왓츠앱">왓츠앱</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">상호명</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    className="form-input"
                    placeholder="상호명 (선택사항)"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">클라이언트명</label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">사업자 여부</label>
                  <select
                    value={formData.is_business ? 'true' : 'false'}
                    onChange={(e) => setFormData({...formData, is_business: e.target.value === 'true'})}
                    className="form-input"
                  >
                    <option value="false">개인</option>
                    <option value="true">사업자</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    {formData.is_business ? '사업자번호' : '주민번호'}
                  </label>
                  <input
                    type="text"
                    value={formData.business_number}
                    onChange={(e) => setFormData({...formData, business_number: e.target.value})}
                    className="form-input"
                    placeholder={formData.is_business ? '사업자번호' : '주민번호'}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">전화번호</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">이메일</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">주소지</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="form-input"
                  placeholder="주소를 입력하세요"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCompany ? '수정' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '7em' }}>MEDIUM</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '9em' }}>상호명</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '9em' }}>클라이언트명</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '5em' }}>TYPE</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '10em' }}>사업자/주민번호</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '8em' }}>전화번호</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '10em' }}>이메일</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '12em' }}>주소지</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '5em' }}>작업</th>
            </tr>
          </thead>
          <tbody>
            {filteredCompanies.map((company) => (
              <tr key={company.id}>
                <td>
                  <span className={`medium-badge ${company.medium || 'default'}`}>
                    {company.medium || '기타'}
                  </span>
                </td>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '9em' }}><strong>{company.company_name}</strong></td>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '9em' }}>{company.client_name}</td>
                <td>
                  <span className={`status-badge ${company.is_business ? '사업자' : '개인'}`}>
                    {company.is_business ? '사업자' : '개인'}
                  </span>
                </td>
                <td style={{ textAlign: 'center', fontSize: '0.8em', padding: '0.5em', width: '10em' }}>{company.business_number || '-'}</td>
                <td style={{ textAlign: 'center', fontSize: '0.8em', padding: '0.5em', width: '8em' }}>{company.phone || '-'}</td>
                <td style={{ textAlign: 'center', fontSize: '0.8em', padding: '0.5em', width: '10em' }}>{company.email || '-'}</td>
                <td style={{ textAlign: 'center', fontSize: '0.8em', padding: '0.5em', width: '12em' }}>{company.address || '-'}</td>
                <td style={{ padding: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3em' }}>
                    <Edit
                      size={20}
                      style={{ cursor: 'pointer', color: '#6c757d', margin: 0, padding: 0 }}
                      onClick={() => handleEdit(company)}
                    />
                    <Trash2
                      size={20}
                      style={{ cursor: 'pointer', color: '#e74c3c', margin: 0, padding: 0 }}
                      onClick={() => handleDelete(company.id)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <p>총 {filteredCompanies.length}개 업체</p>
      </div>
    </div>
  );
}

export default CompanyManagement; 